import { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ARTIFACT_KINDS,
  LINK_MODES,
  PYTHON_ABIS,
  SERVING_TARGET_FLAGS,
  SERVING_TARGETS,
  type ArtifactKind,
  type LinkMode,
  type LinkSummary,
  type PythonAbi,
} from '../../shared/api';
import { type ApiClient, ApiError, NetworkError } from '../api';
import {
  CommandInputError,
  generateInstallCommand,
  inferArtifactKind,
  inferPythonAbi,
  kindUsesServingTarget,
  parseManualObjectPath,
} from '../commands';
import CopyButton from '../components/CopyButton';
import ExpirySelect from '../components/ExpirySelect';
import QrCode from '../components/QrCode';
import SignInPanel from '../components/SignInPanel';
import { sessionApi, sessionStore, type SessionStore, useSessionSnapshot } from '../state';

export interface CommandsPageProps {
  /** Injectable for tests; defaults to the app-wide {@link sessionStore} singleton. */
  store?: SessionStore;
  /** Injectable for tests; defaults to the token-bound {@link sessionApi} client. */
  api?: ApiClient;
}

const ARTIFACT_KIND_LABELS: Record<ArtifactKind, string> = {
  core_deb: 'Core engine DEB',
  trt_plugin_deb: 'TensorRT-LLM plugin DEB',
  frozen_zip: 'Frozen release ZIP',
  stable_zip: 'Stable release ZIP',
  scheduler_wheel: 'Scheduler wheel (pip)',
  decoder_tarball: 'Decoder tarball',
};

const MODE_LABELS: Record<LinkMode, string> = {
  managed: 'Managed link (revocable)',
  gcs_v4: 'Direct GCS V4 URL (non-revocable)',
};

/**
 * Where the command's target comes from.
 *
 * `artifact` — the release selector's reconciled artifact (the normal path).
 * `manual`   — a GCS object path typed or pasted by the operator. This exists
 *              because reconciliation is not the only way an object reaches the
 *              bucket: a hotfix DEB uploaded out-of-band, an object whose name
 *              falls outside `classifyArtifactName`'s taxonomy, or a release
 *              whose GitHub half is unavailable all leave a real, downloadable
 *              object with no row in the artifact cache. Manual issuance goes
 *              through the same POST /api/links allowlist as everything else —
 *              it skips reconciliation, not authorization.
 */
type CommandSource = 'artifact' | 'manual';

/** The fully-resolved inputs one command is generated from. */
interface CommandTarget {
  kind: ArtifactKind;
  pythonAbi: PythonAbi | null;
  /** Allowlisted GCS object path, or a GitHub `<tag>/<asset>` pseudo-path. */
  objectPath: string;
  /** Reconciled artifact id, when the target came from the artifact cache. */
  artifactId?: string;
  /** `gs://…` URI when the operator supplied one (enables the gcloud flow). */
  gsUri: string | null;
  /** True when POST /api/links will accept this path. */
  issuable: boolean;
}

/** A generated command plus the URL it embeds. */
interface GenerationResult {
  command: string;
  url: string;
  /** Null for the `gs://` flow, which issues nothing. */
  link: LinkSummary | null;
  revocable: boolean;
  /** Fingerprint of the inputs the command was generated from (staleness check). */
  inputsKey: string;
}

function formatBytes(sizeBytes: number | null): string {
  if (sizeBytes === null || !Number.isFinite(sizeBytes) || sizeBytes < 0) return 'unknown size';
  const units = ['B', 'KiB', 'MiB', 'GiB'] as const;
  let value = sizeBytes;
  let unit = 0;
  while (value >= 1024 && unit < units.length - 1) {
    value /= 1024;
    unit += 1;
  }
  return `${unit === 0 ? value : value.toFixed(1)} ${units[unit]}`;
}

function describeError(err: unknown): string {
  if (err instanceof ApiError) return `${err.code} (HTTP ${err.status}): ${err.message}`;
  if (err instanceof NetworkError) return err.message;
  if (err instanceof Error) return err.message;
  return String(err);
}

/**
 * Page 2 — install command generator (spec §5, §8 src/views/commands.ts).
 *
 * Flow: the operator either picks a release/artifact on the Releases page
 * (session selection) or names a GCS object directly here, chooses ABI /
 * serving target / expiry / link mode, and clicks Generate. The page issues a
 * download link (POST /api/links), records it in the session, then builds the
 * shell command client-side via {@link generateInstallCommand}. Clipboard copy
 * and the QR code are client-only; the QR payload is the download URL, never a
 * secret (spec §5).
 *
 * The `gs://` flow is the one path that issues nothing: it emits a
 * `gcloud storage cp` command for an operator who already holds bucket
 * credentials, so no capability URL is minted and nothing needs revoking.
 */
export default function CommandsPage({ store = sessionStore, api = sessionApi }: CommandsPageProps) {
  const snapshot = useSessionSnapshot(store);
  const artifact = snapshot.artifact;

  const [source, setSource] = useState<CommandSource>('artifact');
  const [manualInput, setManualInput] = useState('');
  const [manualKind, setManualKind] = useState<ArtifactKind | null>(null);
  const [mode, setMode] = useState<LinkMode>('managed');
  const [issuing, setIssuing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<GenerationResult | null>(null);
  const abortRef = useRef<AbortController | null>(null);

  // Abort any in-flight issuance when the page unmounts; read the ref at
  // cleanup time so the controller created by the latest Generate is seen.
  useEffect(() => {
    const ref = abortRef;
    return () => ref.current?.abort();
  }, []);

  const manualParse = useMemo(
    () => (manualInput.trim() === '' ? null : parseManualObjectPath(manualInput)),
    [manualInput],
  );
  const manualPath = manualParse !== null && manualParse.ok ? manualParse.objectPath : null;
  // Kind follows the pasted path until the operator overrides it, and the
  // override is dropped when the path changes (a new object, a new guess).
  const manualEffectiveKind: ArtifactKind =
    manualKind ?? (manualPath === null ? 'core_deb' : inferArtifactKind(manualPath));
  useEffect(() => {
    setManualKind(null);
  }, [manualPath]);

  const target: CommandTarget | null = useMemo(() => {
    if (source === 'artifact') {
      if (artifact === null) return null;
      return {
        kind: artifact.kind,
        pythonAbi: artifact.pythonAbi,
        objectPath: artifact.objectPath,
        artifactId: artifact.id,
        gsUri: null,
        // GitHub artifact paths are `<tag>/<asset>`, outside the object-path
        // allowlist — they are downloaded from their public URL, not issued.
        issuable: artifact.provider === 'gcs',
      };
    }
    if (manualParse === null || !manualParse.ok) return null;
    return {
      kind: manualEffectiveKind,
      pythonAbi: inferPythonAbi(manualParse.objectPath),
      objectPath: manualParse.objectPath,
      gsUri: manualParse.gsUri,
      issuable: true,
    };
  }, [source, artifact, manualParse, manualEffectiveKind]);

  const effectiveAbi: PythonAbi = target?.pythonAbi ?? snapshot.pythonAbi;

  const inputsKey = JSON.stringify([
    source,
    snapshot.version,
    target?.objectPath ?? null,
    target?.kind ?? null,
    effectiveAbi,
    snapshot.servingTarget,
    snapshot.expiry,
    mode,
  ]);
  const stale = result !== null && result.inputsKey !== inputsKey;

  /** Build the command for `downloadUrl`, or surface the generator's rejection. */
  const buildCommand = (downloadUrl: string, resolved: CommandTarget): string =>
    generateInstallCommand({
      artifact: { kind: resolved.kind, pythonAbi: resolved.pythonAbi },
      downloadUrl,
      servingTarget: snapshot.servingTarget,
      pythonAbi: effectiveAbi,
    });

  const handleGenerate = async () => {
    if (target === null || issuing) return;
    if (!target.issuable) {
      setError(
        'This artifact is a GitHub release asset — download it from its public URL on the ' +
          'Releases page. Only GCS objects can have links issued.',
      );
      return;
    }
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    setIssuing(true);
    setError(null);
    try {
      const response = await api.createLink(
        {
          objectPath: target.objectPath,
          ...(target.artifactId !== undefined ? { artifactId: target.artifactId } : {}),
          mode,
          expiry: snapshot.expiry,
        },
        controller.signal,
      );
      store.recordIssuedLink(response);
      // Managed links come back as /api/download/<token>; the command runs on
      // a different machine, so absolutize against the deployment origin.
      const absoluteUrl = new URL(api.resolveUrl(response.url), window.location.origin).toString();
      setResult({
        command: buildCommand(absoluteUrl, target),
        url: absoluteUrl,
        link: response.link,
        revocable: response.revocable,
        inputsKey,
      });
    } catch (err) {
      if (err instanceof DOMException && err.name === 'AbortError') return;
      setError(describeError(err));
    } finally {
      setIssuing(false);
    }
  };

  /** Emit a `gcloud storage cp` command against the pasted gs:// URI. */
  const handleGenerateFromGsUri = () => {
    if (target === null || target.gsUri === null) return;
    setError(null);
    try {
      setResult({
        command: buildCommand(target.gsUri, target),
        url: target.gsUri,
        link: null,
        revocable: false,
        inputsKey,
      });
    } catch (err) {
      setError(
        err instanceof CommandInputError ? `${err.code}: ${err.message}` : describeError(err),
      );
    }
  };

  if (snapshot.token === null) {
    return (
      <section className="commands-page">
        <h1>Install command generator</h1>
        <SignInPanel
          reason="Issuing download links and generating install commands requires an operator token."
          store={store}
        />
      </section>
    );
  }

  return (
    <section className="commands-page">
      <h1>Install command generator</h1>

      <fieldset className="commands-field commands-source" aria-label="Command target source">
        <legend>Target</legend>
        <label className="commands-radio">
          <input
            type="radio"
            name="command-source"
            value="artifact"
            checked={source === 'artifact'}
            onChange={() => setSource('artifact')}
          />
          Reconciled artifact (from the Releases page)
        </label>
        <label className="commands-radio">
          <input
            type="radio"
            name="command-source"
            value="manual"
            checked={source === 'manual'}
            onChange={() => setSource('manual')}
          />
          Manual GCS object (issue from a path or gs:// URI)
        </label>
      </fieldset>

      {source === 'artifact' && artifact === null && (
        <div className="commands-empty" role="note">
          <p>
            No artifact selected. Pick a release and artifact on the{' '}
            <Link to="/releases">Releases page</Link>, then return here to generate the install
            command — or switch the target above to <strong>Manual GCS object</strong> and name the
            object directly.
          </p>
        </div>
      )}

      {source === 'artifact' && artifact !== null && (
        <section className="commands-selection" aria-label="Selected artifact">
          <div className="commands-selection-header">
            <h2>
              {snapshot.version} — {ARTIFACT_KIND_LABELS[artifact.kind]}
            </h2>
            <Link to="/releases" className="commands-change">
              Change
            </Link>
          </div>
          <dl className="commands-facts">
            <div>
              <dt>Provider</dt>
              <dd>{artifact.provider === 'gcs' ? 'GCS' : 'GitHub'}</dd>
            </div>
            <div>
              <dt>Python ABI</dt>
              <dd>{artifact.pythonAbi ?? 'any'}</dd>
            </div>
            <div>
              <dt>Size</dt>
              <dd>{formatBytes(artifact.sizeBytes)}</dd>
            </div>
            <div>
              <dt>Updated</dt>
              <dd>{new Date(artifact.updatedAt).toLocaleString()}</dd>
            </div>
            <div className="commands-facts-wide">
              <dt>Object path</dt>
              <dd>
                <code>{artifact.objectPath}</code>
              </dd>
            </div>
            {artifact.checksums.sha256Hex !== undefined && (
              <div className="commands-facts-wide">
                <dt>SHA-256</dt>
                <dd>
                  <code className="commands-sha">{artifact.checksums.sha256Hex}</code>
                </dd>
              </div>
            )}
          </dl>
          {artifact.provider === 'github' && (
            <p className="commands-warning" role="note">
              GitHub release assets are served from their own public URL and cannot have links
              issued — only GCS objects can. Use the artifact&rsquo;s public URL on the{' '}
              <Link to="/releases">Releases page</Link>, or switch to a GCS artifact.
            </p>
          )}
        </section>
      )}

      {source === 'manual' && (
        <section className="commands-manual" aria-label="Manual GCS object">
          <div className="commands-field commands-facts-wide">
            <label htmlFor="commands-manual-path">GCS object path, gs:// URI, or console URL</label>
            <input
              id="commands-manual-path"
              type="text"
              className="commands-manual-input"
              value={manualInput}
              spellCheck={false}
              autoComplete="off"
              placeholder="releases/inference-engine/v/1.4.2/py3.10/tnd-install-linux.zip"
              onChange={(event) => setManualInput(event.target.value)}
            />
            <p className="commands-note">
              Accepted:{' '}
              <code>releases/inference-engine/v/&lt;version&gt;/py3.10|py3.12/&lt;file&gt;</code>,{' '}
              <code>debs/run-&lt;id&gt;/py3.10|py3.12/&lt;file&gt;.deb</code>, a{' '}
              <code>gs://bucket/&lt;path&gt;</code> URI, or a{' '}
              <code>storage.googleapis.com</code> URL. Any query string is discarded.
            </p>
          </div>

          {manualParse !== null && !manualParse.ok && (
            <p className="commands-error" role="alert">
              {manualParse.message}
            </p>
          )}

          {manualParse !== null && manualParse.ok && (
            <dl className="commands-facts">
              <div className="commands-facts-wide">
                <dt>Object path</dt>
                <dd>
                  <code>{manualParse.objectPath}</code>
                </dd>
              </div>
              <div>
                <dt>Python ABI</dt>
                <dd>{inferPythonAbi(manualParse.objectPath) ?? 'not in path'}</dd>
              </div>
              <div>
                <dt>Reconciled</dt>
                <dd>no — issued straight from the object path</dd>
              </div>
            </dl>
          )}

          <div className="commands-field">
            <label htmlFor="commands-manual-kind">Install template</label>
            <select
              id="commands-manual-kind"
              value={manualEffectiveKind}
              onChange={(event) => setManualKind(event.target.value as ArtifactKind)}
            >
              {ARTIFACT_KINDS.map((kind) => (
                <option key={kind} value={kind}>
                  {ARTIFACT_KIND_LABELS[kind]}
                </option>
              ))}
            </select>
            <p className="commands-note">
              Guessed from the file extension; override it if the object does not follow the
              release naming convention.
            </p>
          </div>
        </section>
      )}

      {target !== null && (
        <>
          <section className="commands-controls" aria-label="Command inputs">
            <div className="commands-field">
              <label htmlFor="commands-abi">Python ABI</label>
              {target.pythonAbi !== null ? (
                <span className="commands-locked" title="Pinned by the selected artifact">
                  {target.pythonAbi} (pinned by artifact)
                </span>
              ) : (
                <select
                  id="commands-abi"
                  value={snapshot.pythonAbi}
                  onChange={(event) => store.setPythonAbi(event.target.value as PythonAbi)}
                >
                  {PYTHON_ABIS.map((abi) => (
                    <option key={abi} value={abi}>
                      {abi}
                    </option>
                  ))}
                </select>
              )}
            </div>

            <fieldset
              className="commands-field commands-targets"
              disabled={!kindUsesServingTarget(target.kind)}
            >
              <legend>Serving target (tnd-setup)</legend>
              {SERVING_TARGETS.map((servingTarget) => (
                <label key={servingTarget} className="commands-radio">
                  <input
                    type="radio"
                    name="serving-target"
                    value={servingTarget}
                    checked={snapshot.servingTarget === servingTarget}
                    onChange={() => store.setServingTarget(servingTarget)}
                  />
                  <code>{SERVING_TARGET_FLAGS[servingTarget]}</code>
                </label>
              ))}
              {!kindUsesServingTarget(target.kind) && (
                <p className="commands-note">
                  tnd-setup runs only for DEB/ZIP installs; this artifact's command has no serving
                  flag.
                </p>
              )}
            </fieldset>

            <div className="commands-field">
              <label htmlFor="commands-expiry">Link expiry</label>
              <ExpirySelect
                id="commands-expiry"
                value={snapshot.expiry}
                onChange={(expiry) => store.setExpiry(expiry)}
              />
            </div>

            <fieldset className="commands-field">
              <legend>Link mode</legend>
              {LINK_MODES.map((candidate) => {
                // Direct V4 signing needs a GCS object; nothing else can be signed.
                const gcsOnly = candidate === 'gcs_v4' && !target.issuable;
                return (
                  <label
                    key={candidate}
                    className="commands-radio"
                    title={gcsOnly ? 'Direct GCS V4 URLs apply to GCS objects only' : undefined}
                  >
                    <input
                      type="radio"
                      name="link-mode"
                      value={candidate}
                      checked={mode === candidate}
                      disabled={gcsOnly}
                      onChange={() => setMode(candidate)}
                    />
                    {MODE_LABELS[candidate]}
                  </label>
                );
              })}
              {mode === 'gcs_v4' && (
                <p className="commands-warning" role="note">
                  Direct GCS V4 signed URLs are bearer capabilities: non-revocable after issuance
                  (spec §4).
                </p>
              )}
            </fieldset>

            <div className="commands-actions">
              <button
                type="button"
                className="commands-generate"
                onClick={() => {
                  void handleGenerate();
                }}
                disabled={issuing}
              >
                {issuing ? 'Issuing link…' : 'Generate install command'}
              </button>
              {target.gsUri !== null && (
                <button
                  type="button"
                  className="commands-generate-gs"
                  onClick={handleGenerateFromGsUri}
                  disabled={issuing}
                  title="Emits gcloud storage cp against the gs:// URI — no link is issued"
                >
                  Generate gcloud command (no link)
                </button>
              )}
            </div>
          </section>

          {error !== null && (
            <div className="commands-error" role="alert">
              <strong>Generation failed:</strong> {error}
            </div>
          )}

          {result !== null && (
            <section
              className={stale ? 'commands-result commands-result--stale' : 'commands-result'}
              aria-label="Generated install command"
            >
              {stale && (
                <p className="commands-stale" role="note">
                  Inputs changed since this command was generated — the embedded URL is still valid
                  until expiry, but generate again to reflect the new inputs.
                </p>
              )}
              <div className="commands-result-header">
                <h2>Install command</h2>
                <CopyButton text={result.command} label="Copy command" />
              </div>
              <pre className="command-output">
                <code>{result.command}</code>
              </pre>

              <div className="commands-url-row">
                <div className="commands-url-text">
                  <h3>{result.link === null ? 'Object URI' : 'Download URL'}</h3>
                  <code>{result.url}</code>
                  <div className="commands-url-actions">
                    <CopyButton text={result.url} label="Copy URL" />
                  </div>
                </div>
                <QrCode value={result.url} label="QR code encoding the download URL" />
              </div>

              {result.link === null ? (
                <p className="commands-note">
                  No link was issued — this command uses <code>gcloud storage cp</code> and requires
                  the operator running it to hold bucket read access.
                </p>
              ) : (
                <dl className="commands-meta">
                  <div>
                    <dt>Mode</dt>
                    <dd>{MODE_LABELS[result.link.mode]}</dd>
                  </div>
                  <div>
                    <dt>Expires</dt>
                    <dd>{new Date(result.link.expiresAt).toLocaleString()}</dd>
                  </div>
                  <div>
                    <dt>Link id</dt>
                    <dd>
                      <code>{result.link.id}</code>
                    </dd>
                  </div>
                </dl>
              )}
              {result.link !== null && !result.revocable && (
                <p className="commands-warning" role="note">
                  Non-revocable bearer URL — anyone holding it can download until it expires.
                </p>
              )}
            </section>
          )}
        </>
      )}

      {target === null && error !== null && (
        <div className="commands-error" role="alert">
          <strong>Generation failed:</strong> {error}
        </div>
      )}
    </section>
  );
}
